import React, { useState } from 'react';
import {
  Box,
  ButtonIcon,
  Icon,
  Menu,
  MenuItem,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../../shared/hooks';
import type {
  Finding,
  FindingStatus,
  SeverityLevel,
} from '../../data-layer/insightDashboardTypes';
import {
  getPhaseShortLabel,
  getSeverityColor,
} from '../../data-layer/insightDashboardUtils';

interface FindingCardProps {
  finding: Finding;
  isCrossSession: boolean;
  sessionIds: string[];
  onClick: (finding: Finding) => void;
  onStatusChange: (id: number, status: FindingStatus) => void;
  isHighlighted?: boolean;
  isExpanded?: boolean;
  className?: string;
}

const defaultProps: Partial<FindingCardProps> = {
  isHighlighted: undefined,
  isExpanded: undefined,
  className: undefined,
};

const PIPE = '|';

/**
 * Renders a colored badge displaying the severity level of a finding.
 *
 * @param props - Component props.
 * @param props.severity - The severity level to display (e.g. CRITICAL, HIGH, MEDIUM).
 * @returns A styled badge with the severity label.
 */
function SeverityBadge({ severity }: { severity: SeverityLevel }) {
  const color = getSeverityColor(severity);

  return (
    <Box
      className={cx(
        'Sod-FindingCard-severityBadge',
        `Sod-FindingCard-severityBadge--${color}`
      )}>
      <Typography kind='label' className='Sod-FindingCard-severityBadge-text'>
        {severity}
      </Typography>
    </Box>
  );
}

/**
 * Semi-circular arc risk gauge that visually represents a 0–100 risk score.
 * Color transitions from success (low) to warning (medium) to critical (high) based on thresholds.
 * Matches the AAG SodRiskScore arc pattern.
 *
 * @param props - Component props.
 * @param props.score - Risk score between 0 and 100.
 * @returns An SVG arc gauge with the numeric score overlaid.
 */
function RiskGauge({ score }: { score: number }) {
  const percent = Math.min(Math.max(score, 0), 100);
  const size = 40;
  const strokeWidth = 4;
  const radius = (size - strokeWidth) / 2;
  const cx2 = size / 2;
  const cy2 = size / 2;

  // Arc spans 250 degrees (like AAG REGULAR_CONFIG)
  const spanDeg = 250;
  const startAngle = -spanDeg / 2;
  const endAngle = spanDeg / 2;

  // Convert polar to cartesian
  const toCart = (angleDeg: number) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;

    return { x: cx2 + radius * Math.cos(rad), y: cy2 + radius * Math.sin(rad) };
  };

  const arcStart = toCart(endAngle);
  const arcEnd = toCart(startAngle);
  const bgPath = `M ${arcStart.x} ${arcStart.y} A ${radius} ${radius} 0 1 0 ${arcEnd.x} ${arcEnd.y}`;

  // Value arc
  const valueAngle = startAngle + (percent / 100) * spanDeg;
  const valEnd = toCart(valueAngle);
  const largeArc = valueAngle - startAngle > 180 ? 1 : 0;
  // Value arc goes from arcEnd (start of gauge) to valEnd
  const valPath = `M ${arcEnd.x} ${arcEnd.y} A ${radius} ${radius} 0 ${largeArc} 1 ${valEnd.x} ${valEnd.y}`;

  // Color based on score — matches AAG SodRiskScore kinds
  let strokeColor = 'var(--color-success-700, #028329)';
  let textColor = 'var(--color-success-700, #028329)';

  if (percent >= 80) {
    strokeColor = 'var(--color-critical-700, #cc1c10)';
    textColor = 'var(--color-critical-700, #cc1c10)';
  } else if (percent >= 50) {
    strokeColor = 'var(--color-warning-400, #f6a91c)';
    textColor = 'var(--color-warning-400, #f6a91c)';
  }

  return (
    <div className='Sod-FindingCard-riskArc'>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        fill='none'>
        <path
          d={bgPath}
          stroke='var(--color-border-neutral-decorative, #e5e7eb)'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          fill='none'
        />
        {percent > 0 && (
          <path
            d={valPath}
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap='round'
            fill='none'
          />
        )}
      </svg>
      <span
        className='Sod-FindingCard-riskArc-value'
        style={{ color: textColor }}>
        {score}
      </span>
    </div>
  );
}

/**
 * Renders a compact finding row: a primary control expands/collapses details; a separate menu
 * holds status actions (Mark reviewed, Escalate).
 *
 * @param props - Component props.
 * @param props.finding - The finding data to display.
 * @param props.isCrossSession - Whether the finding spans multiple sessions.
 * @param props.sessionIds - Session IDs associated with the finding.
 * @param props.onClick - Callback invoked when the expand control is activated.
 * @param props.onStatusChange - Callback invoked when the user selects a status action (`'reviewed'` or `'escalated'`).
 * @param props.isHighlighted - When true, applies a pulse highlight animation. Defaults to `false`.
 * @param props.isExpanded - When true, applies expanded styling and rotates the chevron. Defaults to `false`.
 * @param props.className - Additional CSS class for the root element.
 * @returns The rendered finding card.
 */
function FindingCard({
  finding,
  isCrossSession,
  sessionIds,
  onClick,
  onStatusChange,
  isHighlighted = false,
  isExpanded = false,
  className,
}: FindingCardProps) {
  const l = useInvertedLocalize();
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const statusLabel =
    finding.status.charAt(0).toUpperCase() + finding.status.slice(1);

  return (
    <div
      className={cx('Sod-FindingCard', className, {
        'Sod-FindingCard--highlighted': isHighlighted,
        'Sod-FindingCard--expanded': isExpanded,
      })}
      data-finding-id={finding.id}>
      <button
        type='button'
        className='Sod-FindingCard-expand'
        aria-expanded={isExpanded}
        aria-label={l('Expand or collapse finding {title}', {
          title: finding.title,
        })}
        onClick={() => {
          onClick(finding);
        }}>
        <Box className='Sod-FindingCard-content'>
          <Typography kind='body1' className='Sod-FindingCard-title'>
            {finding.title}
          </Typography>
          <span className='Sod-FindingCard-pipe'>{PIPE}</span>
          <Box className='Sod-FindingCard-phaseTag'>
            <Typography kind='label' className='Sod-FindingCard-phaseTag-text'>
              {getPhaseShortLabel(finding.phase)}
            </Typography>
          </Box>
          <span className='Sod-FindingCard-pipe'>{PIPE}</span>
          <Box
            className={cx(
              'Sod-FindingCard-status',
              `Sod-FindingCard-status--${finding.status}`
            )}>
            <Typography kind='label' className='Sod-FindingCard-status-text'>
              {statusLabel}
            </Typography>
          </Box>
          {isCrossSession && (
            <>
              <span className='Sod-FindingCard-pipe'>{PIPE}</span>
              {sessionIds.map((sid) => (
                <Box key={sid} className='Sod-FindingCard-sessionBadge'>
                  <Typography
                    kind='label'
                    className='Sod-FindingCard-sessionBadge-text'>
                    {sid}
                  </Typography>
                </Box>
              ))}
            </>
          )}
        </Box>
        <RiskGauge score={finding.riskScore} />
        <span
          className={cx('Sod-FindingCard-chevron', {
            'Sod-FindingCard-chevron--expanded': isExpanded,
          })}
          aria-hidden>
          {'\u25B6'}
        </span>
      </button>
      <div className='Sod-FindingCard-actions'>
        <Menu
          CustomMenu={
            <Box className='Sod-FindingCard-actionsMenu'>
              <MenuItem
                option={{
                  label: l('Mark reviewed'),
                  value: 'reviewed',
                  isDisabled: finding.status === 'reviewed',
                }}
                onClick={() => {
                  onStatusChange(finding.id, 'reviewed');
                  setIsActionsMenuOpen(false);
                }}
                prefixIcon={<Icon kind='CheckCircleFilled' size='small' />}
              />
              <MenuItem
                option={{
                  label: l('Escalate'),
                  value: 'escalated',
                  isDisabled: finding.status === 'escalated',
                }}
                onClick={() => {
                  onStatusChange(finding.id, 'escalated');
                  setIsActionsMenuOpen(false);
                }}
                prefixIcon={<Icon kind='AlertCriticalFilled' size='small' />}
              />
            </Box>
          }
          isPortalEnabled
          isMenuExpanded={isActionsMenuOpen}
          setIsExpanded={setIsActionsMenuOpen}
          placement='bottom-end'
          trigger={
            <ButtonIcon
              kind='ghost'
              size='small'
              ariaLabel={l('Finding actions')}
              icon={<Icon kind='MoreVertical' size='small' />}
            />
          }
        />
      </div>
    </div>
  );
}

FindingCard.defaultProps = defaultProps;

export default FindingCard;
export { RiskGauge, SeverityBadge };
